import styled, { css, CSSProperties } from 'styled-components';

import { rem } from '../../libs';
import { sizeStyles, StyledSizeProps } from '../../styles';
import { fontSizes } from '../../theme';
import colors from '../../theme/constants/colors';
import { ValueOf } from '../../types';
import InputButtonPrimitive from '../Input/primitives/InputButtonPrimitive';

import { RadioButtonClassNames, RadioButtonVariableNames } from './constants';
import { RadioButtonMode } from './type';

const TRANSITION_DURATION_MS = 300;

const variableStyles = css(
  ({
    theme,
  }): Record<ValueOf<typeof RadioButtonVariableNames>, string | undefined> => ({
    [RadioButtonVariableNames.InputSize]: rem(20),
    [RadioButtonVariableNames.HoverPadding]: theme.core.spacing['10x'],
    [RadioButtonVariableNames.InputPadding]: theme.core.spacing['5x'],
    [RadioButtonVariableNames.TransitionDuration]: `${TRANSITION_DURATION_MS}ms`,
  }),
);

type RadioButtonRootProps = {
  $isDisabled: boolean | undefined;
  $whiteSpace: CSSProperties['whiteSpace'];
  $mode: RadioButtonMode;
} & StyledSizeProps;

export const RadioButtonRoot = styled.label<RadioButtonRootProps>(
  ({ theme, $isDisabled, $whiteSpace = 'nowrap', $mode }) => {
    const variant = theme.components.RadioButton.mode[$mode];

    return css`
      ${variableStyles}

      position: relative;
      z-index: 0;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      padding: var(${RadioButtonVariableNames.InputPadding});
      cursor: ${$isDisabled ? 'not-allowed' : 'pointer'};
      transition: background-color
        var(${RadioButtonVariableNames.TransitionDuration}) ease-in-out;
      user-select: none;
      width: max-content;
      max-width: 100%;
      overflow: hidden;

      white-space: ${$whiteSpace};

      &:hover {
        .${RadioButtonClassNames.Main} {
          &::before {
            --radio-button-input-hover-size: calc(
              var(${RadioButtonVariableNames.InputSize}) +
                var(${RadioButtonVariableNames.HoverPadding})
            );
            content: '';
            box-sizing: border-box;
            display: inline-block;
            position: absolute;
            z-index: -2;
            width: var(--radio-button-input-hover-size);
            height: var(--radio-button-input-hover-size);
            border-radius: ${theme.core.radius.full};
            background-color: ${!$isDisabled && variant['hit-area-hover']};
          }
        }
      }

      ${sizeStyles}
    `;
  },
);

type RadioButtonInputProps = {
  $hasError?: boolean;
  $mode: RadioButtonMode;
};

const finishedInputStyles = css`
  background-color: transparent;
  border: 1px solid transparent;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 4px ${colors.sand};
  }
`;

const emptyInputStyles = css<RadioButtonInputProps>(
  ({ theme, $hasError = false }) => {
    const emptyVariant = theme.components.RadioButton.mode.empty;

    return css`
      border: 1px solid ${emptyVariant['border-default']};

      ${$hasError &&
      css`
        border-color: ${emptyVariant['border-error']};
        outline: 1px solid ${emptyVariant['border-error']};
      `}

      &:focus-visible {
        background-color: ${emptyVariant['bg-focus']};
        box-shadow: 0 0 0 4px ${colors.sand};
      }

      &:disabled {
        border-color: ${emptyVariant['border-disabled']};
      }
    `;
  },
);

const checkedInputStyles = css(({ theme }) => {
  const checkedVariant = theme.components.RadioButton.mode.checked;

  return css`
    border: 6px solid ${checkedVariant['bg-default']};
    background-color: ${checkedVariant['icon-default']};
    outline: none;

    &:focus-visible {
      box-shadow: 0 0 0 4px ${colors.mouse};
    }

    &:disabled {
      background-color: ${checkedVariant['icon-disabled']};
      border-color: ${checkedVariant['bg-disabled']};
    }
  `;
});

export const RadioButtonInput = styled(
  InputButtonPrimitive,
)<RadioButtonInputProps>(
  ({ theme, $mode }) => css<RadioButtonInputProps>`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: var(${RadioButtonVariableNames.InputSize});
    height: var(${RadioButtonVariableNames.InputSize});
    margin: 0;
    border-radius: ${theme.core.radius.full};
    transition: border var(${RadioButtonVariableNames.TransitionDuration})
      ease-in-out;
    appearance: none;
    outline-offset: 0;
    outline: none;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }

    ${$mode === 'finished' ? finishedInputStyles : emptyInputStyles}

    &:checked {
      ${checkedInputStyles}
    }
  `,
);

export const RadioButtonLabel = styled.span`
  margin-left: ${({ theme }) => theme.core.spacing['6x']};
  color: ${colors.navy};
  font-size: ${fontSizes.m};
  line-height: 1.25;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const RadioButtonFinishedOverlayRoot = styled.span<{
  $isDisabled: boolean;
}>(({ theme, $isDisabled }) => {
  const finishedVariant = theme.components.RadioButton.mode.finished;

  return css`
    position: absolute;
    z-index: -1;
    width: var(${RadioButtonVariableNames.InputSize});
    height: var(${RadioButtonVariableNames.InputSize});
    display: flex;
    align-items: center;
    justify-content: center;
    left: var(${RadioButtonVariableNames.InputPadding});
    border-radius: ${theme.core.radius.full};
    color: ${$isDisabled
      ? finishedVariant['icon-disabled']
      : finishedVariant['icon-default']};

    background-color: ${$isDisabled
      ? finishedVariant['bg-disabled']
      : finishedVariant['bg-default']};

    svg {
      fill: currentColor;
    }
  `;
});
